/**
 * A redline's thread, on the plane it was drawn on. Every mark has one
 * (`keepMarkThreads`); selecting a mark opens it under the mark, with the
 * same card, composer and @Bear as a text comment. A callout's lettering is
 * the thread's first message, read off the drawing.
 *
 * Asking Bear sends a picture with the thread: the 3D view behind the plane
 * with its marks drawn over it, the two registered because the plane's
 * camera drives the 3D one square-on.
 */
import type { DocHandle } from "@automerge/automerge-repo";
import {
  COMMENT_CARD_WIDTH,
  CommentThread,
} from "forest/comments/CommentThread";
import { MAX_IMAGE_EDGE_PX } from "playground/agents/model-images";
import {
  markOf,
  markText,
  markThreadId,
  replyToAnnotation,
  setAnnotationResolved,
  threadMessagesOf,
  type AnnotatedDoc,
  type PlaneRecord,
} from "playground/annotations/annotations";
import {
  describeMarks,
  describePlane,
} from "playground/annotations/marks-text";
import type { CSSProperties } from "react";
import { toast } from "sonner";
import { useValue, type Editor } from "tldraw";

import { useAskBear } from "@/comments/ask-bear";
import { BEAR, threadMessageViews } from "@/comments/text-comments";
import { captureModelView } from "@/lib/bear-sandbox-bridge";
import { useSignedInUser } from "@/lib/session";

/** What a mark is, in the words a reviewer would use. */
const MARK_NAMES: Record<string, string> = {
  cloud: "Revision cloud",
  callout: "Callout",
  draw: "Sketch",
  highlight: "Highlight",
  arrow: "Arrow",
  text: "Text",
  rectangle: "Box",
  ellipse: "Ellipse",
};

function markName(mark: PlaneRecord | undefined): string {
  const props = mark?.props as { geo?: unknown } | undefined;
  const kind = typeof props?.geo === "string" ? props.geo : String(mark?.type);
  return MARK_NAMES[kind] ?? "Mark";
}

/** The one drawn mark selected for reading, and where under it the card sits. */
function useSelectedMark(editor: Editor) {
  return useValue(
    "selected mark",
    () => {
      const ids = editor.getSelectedShapeIds();
      const id = ids.length === 1 ? ids[0] : undefined;
      const shape = id && editor.getShape(id);
      if (
        !shape ||
        shape.parentId !== editor.getCurrentPageId() ||
        editor.getEditingShapeId() ||
        editor.getCurrentToolId() !== "select"
      ) {
        return null;
      }
      const bounds = editor.getShapePageBounds(shape);
      if (!bounds) return null;
      // Under the mark, or over it when the mark sits low; always in view.
      const { w, h } = editor.getViewportScreenBounds();
      const top = editor.pageToViewport({ x: bounds.minX, y: bounds.minY });
      const bottom = editor.pageToViewport({ x: bounds.minX, y: bounds.maxY });
      const left = Math.max(8, Math.min(top.x, w - COMMENT_CARD_WIDTH - 8));
      const place: CSSProperties =
        bottom.y > h / 2
          ? { left, bottom: Math.max(8, h - top.y + 8) }
          : { left, top: bottom.y + 8 };
      return { id: shape.id, place };
    },
    [editor],
  );
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.src = src;
  await image.decode();
  return image;
}

/** The 3D view with the plane's marks over it, at the size models read. */
async function captureMarkup(editor: Editor): Promise<string> {
  const model = await loadImage(await captureModelView());
  const shapes = [...editor.getCurrentPageShapeIds()];
  const marks = shapes.length
    ? await editor.toImage(shapes, {
        format: "png",
        background: false,
        bounds: editor.getViewportPageBounds(),
        padding: 0,
      })
    : null;
  const scale = Math.min(
    1,
    MAX_IMAGE_EDGE_PX / Math.max(model.width, model.height),
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(model.width * scale);
  canvas.height = Math.round(model.height * scale);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser can't compose a picture");
  context.drawImage(model, 0, 0, canvas.width, canvas.height);
  if (marks) {
    const url = URL.createObjectURL(marks.blob);
    try {
      context.drawImage(
        await loadImage(url),
        0,
        0,
        canvas.width,
        canvas.height,
      );
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  return canvas.toDataURL("image/webp", 0.85);
}

export function MarkThreadCard({
  editor,
  handle,
  doc,
  planeId,
  file,
}: {
  editor: Editor;
  handle: DocHandle<AnnotatedDoc>;
  doc: AnnotatedDoc;
  planeId: string;
  /** The file the plane belongs to, as the agent reads it. */
  file: string;
}) {
  const user = useSignedInUser();
  const askBear = useAskBear();
  const selected = useSelectedMark(editor);
  const threadId = selected && markThreadId(selected.id);
  const annotation = threadId ? doc[`comment:${threadId}`] : undefined;
  if (!selected || !threadId || !annotation) return null;

  const mark = markOf(doc, annotation);
  const name = markName(mark);
  const text = markText(mark);
  const messages = threadMessagesOf(doc, annotation);

  const post = (body: string, mentions: string[]) => {
    replyToAnnotation(handle, threadId, {
      author: user.id,
      authorName: user.name,
      body,
      mentions,
    });
    if (!askBear || !mentions.includes(BEAR.mention)) return;
    void (async () => {
      const after = handle.doc();
      const thread = after[`comment:${threadId}`];
      if (!thread) return;
      const plane = after[`plane:${planeId}`];
      await askBear(
        {
          path: file,
          threadId,
          on: [
            text
              ? `A ${name.toLowerCase()} reading "${text}", the starred mark below.`
              : `A ${name.toLowerCase()}, the starred mark below.`,
            plane ? describePlane(plane) : "",
            plane ? describeMarks(plane, mark?.id) : "",
            "A picture of the model square-on to the plane, with its marks, is attached.",
          ]
            .filter(Boolean)
            .join("\n"),
          messages: threadMessagesOf(after, thread).map(
            ({ authorName, body: said }) => ({
              authorName: authorName ?? "Member",
              body: said,
            }),
          ),
        },
        await captureMarkup(editor),
      );
    })().catch((error: unknown) =>
      toast.error(
        error instanceof Error ? error.message : "Could not ask Bear",
      ),
    );
  };

  return (
    <div
      className="absolute z-[500]"
      style={selected.place}
      // The card is not the canvas: pointer and wheel stay in it.
      onPointerDown={(event) => event.stopPropagation()}
      onWheel={(event) => event.stopPropagation()}
    >
      <CommentThread
        excerpt={name}
        outdated={mark ? undefined : "This mark has been erased."}
        messages={threadMessageViews(messages, user)}
        resolved={annotation.resolved}
        agent={askBear ? BEAR : undefined}
        onResolvedChange={(resolved) =>
          setAnnotationResolved(handle, threadId, resolved)
        }
        onPost={({ body, mentions }) => post(body, mentions)}
        onClose={() => editor.selectNone()}
        followsSelection
      />
    </div>
  );
}
